const STORAGE_PREFIX = "ticketing:page-video-popup";
const SNOOZE_STORAGE_PREFIX = `${STORAGE_PREFIX}:snooze`;
const DEFAULT_SNOOZE_MS = 24 * 60 * 60 * 1000;

export const createPopupUserNamespace = (issuer: string, subject: string) =>
  encodeURIComponent(`${issuer}|${subject}`);

export const createPopupStorageKey = (
  userNamespace: string,
  popupKey: string,
  version: number,
) =>
  `${STORAGE_PREFIX}:${userNamespace}:${encodeURIComponent(popupKey)}:${version}`;

export const createPopupSnoozeStorageKey = (
  userNamespace: string,
  popupKey: string,
  version: number,
) =>
  `${SNOOZE_STORAGE_PREFIX}:${userNamespace}:${encodeURIComponent(
    popupKey,
  )}:${version}`;

export const hasPopupSuppression = (
  storage: Storage,
  userNamespace: string,
  popupKey: string,
  version: number,
) => {
  try {
    return (
      storage.getItem(
        createPopupStorageKey(userNamespace, popupKey, version),
      ) !== null
    );
  } catch {
    return false;
  }
};

export const hasPopupSnooze = (
  storage: Storage,
  userNamespace: string,
  popupKey: string,
  version: number,
  now = new Date(),
) => {
  const key = createPopupSnoozeStorageKey(userNamespace, popupKey, version);
  try {
    const value = storage.getItem(key);
    if (!value) return false;
    const expiresAt = new Date(value);
    if (Number.isNaN(expiresAt.getTime()) || expiresAt <= now) {
      storage.removeItem(key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const suppressPopup = (
  storage: Storage,
  userNamespace: string,
  popupKey: string,
  version: number,
) => {
  try {
    storage.setItem(
      createPopupStorageKey(userNamespace, popupKey, version),
      new Date().toISOString(),
    );
    return true;
  } catch {
    return false;
  }
};

export const snoozePopup = (
  storage: Storage,
  userNamespace: string,
  popupKey: string,
  version: number,
  now = new Date(),
  snoozeMs = DEFAULT_SNOOZE_MS,
) => {
  try {
    storage.setItem(
      createPopupSnoozeStorageKey(userNamespace, popupKey, version),
      new Date(now.getTime() + snoozeMs).toISOString(),
    );
    return true;
  } catch {
    return false;
  }
};

export const clearPopupSnoozesForUser = (
  storage: Storage,
  userNamespace: string,
) => {
  try {
    const keyPrefix = `${SNOOZE_STORAGE_PREFIX}:${userNamespace}:`;
    const keysToRemove = Array.from({ length: storage.length }, (_, index) =>
      storage.key(index),
    ).filter((key): key is string => key?.startsWith(keyPrefix) ?? false);
    keysToRemove.forEach((key) => storage.removeItem(key));
    return true;
  } catch {
    return false;
  }
};
